import { useEffect, useRef, useState } from "react";
import {
  Save, Plus, Trash2, ArrowUp, ArrowDown, Upload, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import resizeImage, { IMAGE_ACCEPT, isImageFile } from "@/lib/imageResize";
import LinkableTextarea from "@/components/admin/LinkableTextarea";

const ICON_OPTIONS = [
  "HeartHandshake", "ShieldCheck", "MapPin", "Sparkles", "Home", "Building2",
  "Star", "Truck", "Users", "Clock", "Award", "Leaf",
];


function uid() {
  return `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export default function AdminAbout() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [offeringsText, setOfferingsText] = useState("");

  useEffect(() => {
    api.get("/admin/about")
      .then((r) => {
        setC(r.data);
        setOfferingsText((r.data.offerings || []).join("\n"));
      })
      .catch((e) => toast.error(apiErrorMessage(e)));
  }, []);

  const patch = (k, v) => setC((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const offerings = offeringsText.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/admin/about", { ...c, offerings });
      toast.success("About page saved.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const addCard = () =>
    patch("values", [
      ...(c.values || []),
      { id: uid(), icon: "Sparkles", label: "New card", desc: "", sort_order: (c.values || []).length + 1 },
    ]);

  const removeCard = (id) =>
    patch("values", (c.values || []).filter((v) => v.id !== id));

  const updateCard = (id, key, val) =>
    patch(
      "values",
      (c.values || []).map((v) => (v.id === id ? { ...v, [key]: val } : v)),
    );

  const moveCard = (id, dir) => {
    const list = [...(c.values || [])];
    const idx = list.findIndex((v) => v.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    list.forEach((v, i) => (v.sort_order = i + 1));
    patch("values", list);
  };

  if (!c) return <div className="text-sm text-[#475569]">Loading…</div>;

  return (
    <div data-testid="admin-about-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-2xl text-[#0F172A]">About Page</h2>
          <p className="mt-1 text-sm text-[#475569]">
            Edit every part of the public About page. Changes are live after Save + refresh.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-about-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {/* HERO */}
      <Card title="Hero section">
        <Grid>
          <Field label="Badge text">
            <Input value={c.hero_badge} onChange={(v) => patch("hero_badge", v)} testid="about-hero_badge" />
          </Field>
          <Field label="Title (first part)">
            <Input value={c.hero_title} onChange={(v) => patch("hero_title", v)} testid="about-hero_title" />
          </Field>
          <Field label="Title (highlighted part)">
            <Input value={c.hero_title_highlight} onChange={(v) => patch("hero_title_highlight", v)} testid="about-hero_title_highlight" />
          </Field>
          <Field label="Subtitle" full>
            <TextArea value={c.hero_subtitle} onChange={(v) => patch("hero_subtitle", v)} testid="about-hero_subtitle" rows={3} />
          </Field>
          <Field label="Hero image alt text">
            <Input value={c.hero_image_alt} onChange={(v) => patch("hero_image_alt", v)} testid="about-hero_image_alt" />
          </Field>
        </Grid>
        <ImageSlot
          label="Hero image"
          testidPrefix="about-hero-image"
          value={c.hero_image_data}
          onChange={(v) => patch("hero_image_data", v)}
          fallbackHint="Empty falls back to the current stock hero image."
        />
      </Card>

      {/* MAIN */}
      <Card title="Main section (What we do)">
        <Grid>
          <Field label="Eyebrow">
            <Input value={c.main_eyebrow} onChange={(v) => patch("main_eyebrow", v)} testid="about-main_eyebrow" />
          </Field>
          <Field label="Heading">
            <Input value={c.main_heading} onChange={(v) => patch("main_heading", v)} testid="about-main_heading" />
          </Field>
          <Field label="Body" full>
            <LinkableTextarea value={c.main_body} onChange={(v) => patch("main_body", v)} testid="about-main_body" rows={4} />
          </Field>
          <Field label="Service list (one per line; names that match an approved service page link automatically)" full>
            <LinkableTextarea value={offeringsText} onChange={setOfferingsText} testid="about-offerings" rows={6} />
          </Field>
          <Field label="Optional secondary image alt text">
            <Input value={c.main_image_alt} onChange={(v) => patch("main_image_alt", v)} testid="about-main_image_alt" />
          </Field>
        </Grid>
        <ImageSlot
          label="Optional secondary image"
          testidPrefix="about-main-image"
          value={c.main_image_data}
          onChange={(v) => patch("main_image_data", v)}
          fallbackHint="Optional. Leave empty to hide."
        />
      </Card>

      {/* VALUES */}
      <Card title="Feature cards">
        <Grid>
          <Field label="Eyebrow">
            <Input value={c.values_eyebrow} onChange={(v) => patch("values_eyebrow", v)} testid="about-values_eyebrow" />
          </Field>
          <Field label="Section heading">
            <Input value={c.values_heading} onChange={(v) => patch("values_heading", v)} testid="about-values_heading" />
          </Field>
        </Grid>

        <div className="mt-4 space-y-3" data-testid="about-values-list">
          {(c.values || []).map((v, i) => (
            <div
              key={v.id}
              data-testid={`about-value-row-${v.id}`}
              className="border border-slate-200 rounded-xl p-4 bg-slate-50/50"
            >
              <div className="grid md:grid-cols-12 gap-3 items-start">
                <div className="md:col-span-3">
                  <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Icon</label>
                  <select
                    value={v.icon || "Sparkles"}
                    onChange={(e) => updateCard(v.id, "icon", e.target.value)}
                    data-testid={`about-value-${v.id}-icon`}
                    className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                  >
                    {ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="md:col-span-4">
                  <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Title</label>
                  <Input value={v.label} onChange={(val) => updateCard(v.id, "label", val)} testid={`about-value-${v.id}-label`} />
                </div>
                <div className="md:col-span-5">
                  <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Description</label>
                  <TextArea value={v.desc} onChange={(val) => updateCard(v.id, "desc", val)} testid={`about-value-${v.id}-desc`} rows={2} />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={() => moveCard(v.id, -1)}
                  disabled={i === 0}
                  data-testid={`about-value-${v.id}-up`}
                  className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-100 disabled:opacity-40"
                  aria-label="Move up"
                ><ArrowUp className="w-3.5 h-3.5" /></button>
                <button
                  type="button"
                  onClick={() => moveCard(v.id, 1)}
                  disabled={i === (c.values || []).length - 1}
                  data-testid={`about-value-${v.id}-down`}
                  className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-100 disabled:opacity-40"
                  aria-label="Move down"
                ><ArrowDown className="w-3.5 h-3.5" /></button>
                <button
                  type="button"
                  onClick={() => removeCard(v.id)}
                  data-testid={`about-value-${v.id}-delete`}
                  className="p-1.5 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50"
                  aria-label="Delete"
                ><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addCard}
          data-testid="admin-about-add-card"
          className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"
        >
          <Plus className="w-4 h-4" /> Add card
        </button>
      </Card>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-about-save-bottom"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

/* ---------- tiny local UI atoms (kept close to the page to avoid new files) ---------- */
function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6">
      <h3 className="font-display font-semibold text-lg text-[#0F172A]">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}
function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
function Input({ value, onChange, testid }) {
  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
    />
  );
}
function TextArea({ value, onChange, testid, rows = 3 }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      rows={rows}
      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
    />
  );
}

function ImageSlot({ label, testidPrefix, value, onChange, fallbackHint }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!isImageFile(file)) return toast.error("Please pick an image file.");
    setBusy(true);
    try {
      const compressed = await resizeImage(file);
      onChange(compressed);
      toast.success("Image ready — click Save to publish.");
    } catch (e) {
      toast.error("Failed to read image.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</p>
      <div className="grid md:grid-cols-[220px,1fr] gap-4 items-start">
        <div className="aspect-[16/10] rounded-xl bg-slate-50 border border-dashed border-slate-200 overflow-hidden flex items-center justify-center">
          {value ? (
            <img
              src={value}
              alt="preview"
              data-testid={`${testidPrefix}-preview`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center text-xs text-slate-400 px-4">
              <ImageIcon className="w-6 h-6 mx-auto mb-1 text-slate-300" />
              No image
            </div>
          )}
        </div>
        <div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid={`${testidPrefix}-upload`}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
            >
              <Upload className="w-4 h-4" /> {value ? "Replace" : "Upload"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                data-testid={`${testidPrefix}-clear`}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={IMAGE_ACCEPT}
              className="hidden"
              data-testid={`${testidPrefix}-file-input`}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
          {fallbackHint && (
            <p className="mt-2 text-xs text-[#475569]">{fallbackHint}</p>
          )}
        </div>
      </div>
    </div>
  );
}
